import { Image } from '@lounge-fe/ui-kit'
import { useMemo } from 'react'
import SelectGroup from '../SelectGroup'
import { LinkedItemSelectionProps } from './types'
import { useFormContext, useWatch } from 'react-hook-form'
import { useVariantUpdate } from '../hooks/useVariantUpdate'
import { isOptionInStock } from '../utils'
import { SelectGroupProps } from '../SelectGroup/types'
import { useDataTranslation } from '@/features/localisation/hooks/useDataTranslation/useDataTranslation'

export const LinkedItemSelection = ({
  title,
  name,
  linkedItems,
  isDisabledSelectable,
}: LinkedItemSelectionProps) => {
  const { t } = useDataTranslation()

  const {
    formState: { errors },
  } = useFormContext()

  const [selectedLinkedItemId, selectedOptions] = useWatch({
    name: [`${name}.id`, `${name}.options`],
  })

  const selectedLinkedItem = useMemo(
    () => linkedItems.find(({ id }) => id === Number(selectedLinkedItemId)),
    [selectedLinkedItemId],
  )

  const memoizedVariants = useMemo(
    () => selectedLinkedItem?.variants ?? [],
    [selectedLinkedItem],
  )

  useVariantUpdate(`${name}.options`, `${name}.variant`, memoizedVariants)

  const hasMultipleLinkedItems = linkedItems.length > 1

  if (!linkedItems.length || !selectedLinkedItem) {
    return null
  }

  return (
    <SelectGroup
      data-testid="linked-item-selection"
      className="mb-6"
      title={title}
      image={
        hasMultipleLinkedItems &&
        selectedLinkedItem.images?.length && (
          <Image
            key={selectedLinkedItem.images[0].src}
            className="object-cover object-center aspect-square w-full"
            data={{
              src: selectedLinkedItem.images[0].src,
              width: 136,
              height: 136,
              crop: 'center',
            }}
            alt={selectedLinkedItem.title}
          />
        )
      }
      fields={
        [
          hasMultipleLinkedItems && {
            control: 'select',
            name: `${name}.id`,
            label: t('product.product'),
            options: linkedItems.map((linkedItem) => ({
              label: linkedItem.title,
              value: linkedItem.id,
            })),
            errors,
          },
          ...selectedLinkedItem.options.map(
            ({ name: optionName, values }, idx) => ({
              control: hasMultipleLinkedItems ? 'select' : 'choicechip',
              name: `${name}.options[${idx}]`,
              label: t(optionName),
              isDisabledSelectable,
              placeholder: t('product.select_an_option'),
              options: values.map((option) => ({
                label: option,
                value: option,
                disabled: !isOptionInStock(
                  option,
                  idx,
                  selectedOptions,
                  selectedLinkedItem?.variants ?? [],
                ),
              })),
              errors,
            }),
          ),
        ].filter(Boolean) as SelectGroupProps['fields']
      }
    />
  )
}
